import { getIn } from 'formik';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { IQuestion } from 'types/Question.types';
import { COLORS } from 'assets/styled';
import { useValidateRadioInput } from './hooks';
import { useSetManuallyChangeQuestionName } from 'pages/ConfigurePage/ConfigureForm/hooks';
import { useDispatch } from 'react-redux';
import ClearValueButton from 'components/ClearValueButton/ClearValueButton';
import { RadioGroupRow } from './RadioInput.styled';

interface IRadioInputProps {
  pwId: string;
  question: IQuestion;
  context: any;
  showClear?: boolean;
  reserveClear?: boolean;
  onClear?: () => void;
}

const RadioInput = ({
  pwId,
  question,
  context,
  showClear = false,
  reserveClear = false,
  onClear,
}: IRadioInputProps) => {
  const dispatch = useDispatch();
  const selectedOptionValue = getIn(context.values, question.initialKey);
  const errorMessage = useValidateRadioInput(question, context);
  const setManuallyChangeQuestionName =
    useSetManuallyChangeQuestionName(dispatch);

  // Four or more options stack, and then the clear button lines up with the
  // question name and the first option rather than with the whole group.
  const isStacked = question.options.length > 3;

  return (
    <RadioGroupRow isStacked={isStacked}>
      <FormControl component="fieldset" error={!!errorMessage}>
        <RadioGroup
          name={question.initialKey}
          value={selectedOptionValue}
          onChange={(event) => {
            const rawValue = event.target.value;

            // MUI Radio returns string values — parse back to original type (number/boolean) by matching stringified option and inferring type from it
            const originalOption = question.options.find(
              (opt) => String(opt[0]) === rawValue
            );

            let parsedValue: string | number | boolean = rawValue;

            if (originalOption) {
              const originalValue = originalOption[0];
              if (typeof originalValue === 'number') {
                parsedValue = Number(rawValue);
              } else if (typeof originalValue === 'boolean') {
                parsedValue = rawValue === 'true';
              }
            }

            context.setFieldValue(question.initialKey, parsedValue);
            setManuallyChangeQuestionName(parsedValue, question.initialKey);
          }}
          row={question.options.length <= 3}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            // Sizes to the options so the group can share the question name's
            // line; at 100% it claims the row and forces itself onto its own.
            alignItems: isStacked ? 'flex-start' : 'center',
            width: 'auto',
            // Three long option labels would overflow the row on a phone
            // rather than drop to a second line; wrapped rows stay right
            // aligned with justifyContent above.
            flexWrap: 'wrap',
          }}
        >
          {question.options.map((option: any[], index: number) => {
            const isChecked = selectedOptionValue === option[0];
            return (
              <FormControlLabel
                key={index}
                value={option[0]}
                control={
                  <Radio
                    checked={isChecked}
                    data-testid={`${pwId}-${index}`}
                    size="small"
                    sx={{
                      color: COLORS.GREEN,
                      '&.Mui-checked': { color: COLORS.GREEN },
                    }}
                  />
                }
                label={option[0]}
                sx={{
                  color: COLORS.BLACK,
                  fontSize: '16rem',
                  fontFamily: 'inherit',
                  '& .MuiTypography-root': {
                    fontSize: '16rem',
                    fontFamily: 'inherit',
                  },
                }}
              />
            );
          })}
        </RadioGroup>
      </FormControl>
      {(showClear || reserveClear) && onClear && (
        <ClearValueButton
          inline
          hidden={!showClear}
          onClear={onClear}
          pwId={pwId + '_clear'}
        />
      )}
    </RadioGroupRow>
  );
};

export default RadioInput;
